import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    // Check for tokens in order of preference
    const backdoorToken = localStorage.getItem('backdoor_token');
    const ssoBackendToken = localStorage.getItem('sso_backend_token');
    
    if (backdoorToken) {
      config.headers.Authorization = `Bearer ${backdoorToken}`;
    } else if (ssoBackendToken) {
      config.headers.Authorization = `Bearer ${ssoBackendToken}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Projects API
export const projectsAPI = {
  getAll: (resourceId = null) => {
    const params = resourceId ? { resource_id: resourceId } : {};
    return api.get('/projects', { params });
  },
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.patch(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getReportData: (id) => api.get(`/projects/${id}/report`),
};

// Companies API
export const companiesAPI = {
  getAll: () => api.get('/companies'),
  create: (data) => api.post('/companies', data),
};

// People API (OSR/ISR)
export const peopleAPI = {
  getAll: () => api.get('/people'),
};

// Tasks API
export const tasksAPI = {
  getAll: (taskGroupId = null, resourceId = null) => {
    const params = {};
    if (taskGroupId) params.task_group_id = taskGroupId;
    if (resourceId) params.resource_id = resourceId;
    return api.get('/tasks', { params });
  },
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.patch(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  getChangelog: (id, limit = 50) => api.get(`/tasks/${id}/changelog`, { params: { limit } }),
  getRevisions: (id) => api.get(`/tasks/${id}/revisions`),
  // Parallel groups
  createParallelGroup: (taskIds) => api.post('/tasks/parallel-group', { task_ids: taskIds, action: 'create' }),
  removeParallelGroup: (taskIds) => api.post('/tasks/parallel-group', { task_ids: taskIds, action: 'remove' }),
  getParallelGroups: (taskGroupId = null) => api.get('/tasks/parallel-groups', { params: taskGroupId ? { task_group_id: taskGroupId } : {} }),
  // Dependencies
  getAvailablePredecessors: (taskId, projectId = null) => api.get(`/tasks/available-predecessors/${taskId}`, { params: projectId ? { project_id: projectId } : {} }),
  getDependencies: (taskId) => api.get(`/tasks/dependencies/${taskId}`),
  checkDependencyImpact: (taskId, predecessorIds) => api.post('/tasks/check-dependency-impact', { task_id: taskId, predecessor_ids: predecessorIds }),
};

// Task Groups API
export const taskGroupsAPI = {
  getAll: () => api.get('/task-groups'),
  getById: (id) => api.get(`/task-groups/${id}`),
  create: (data) => api.post('/task-groups', data),
  update: (id, data) => api.patch(`/task-groups/${id}`, data),
  delete: (id) => api.delete(`/task-groups/${id}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: (resourceId = null) => {
    const params = resourceId ? { resource_id: resourceId } : {};
    return api.get('/dashboard/stats', { params });
  },
  getExecutive: (resourceId = null) => {
    const params = resourceId ? { resource_id: resourceId } : {};
    return api.get('/dashboard/executive', { params });
  },
  getPredictive: () => api.get('/dashboard/predictive'),
};

// Backup/Restore API
export const backupAPI = {
  create: () => api.get('/backup'),
  restore: (backup, mode = 'replace') => api.post('/restore', { backup, mode }),
  // File-based backup/restore (server-side)
  backupToFile: (filename = 'airdash-backup.json') => api.post(`/backup/file?filename=${filename}`),
  restoreFromFile: (filename = 'airdash-backup.json') => api.post(`/restore/file?filename=${filename}`),
  listFiles: () => api.get('/backup/files'),
};

// Reports API
export const reportsAPI = {
  sendWeekly: (roleFilter = null) => api.post('/reports/send-weekly', { role_filter: roleFilter }),
  preview: (resourceId) => api.get(`/reports/preview/${resourceId}`),
};

// Settings API
export const settingsAPI = {
  getEmailReports: () => api.get('/settings/email-reports'),
  updateEmailReports: (settings) => api.put('/settings/email-reports', settings),
};

// Resources/Team API
export const resourcesAPI = {
  getAll: () => api.get('/resources'),
  create: (data) => api.post('/resources', data),
  update: (id, data) => api.patch(`/resources/${id}`, data),
  updateRoles: (id, roles) => api.patch(`/resources/${id}/roles`, { roles }),
  delete: (id) => api.delete(`/resources/${id}`),
};

// Roles API
export const rolesAPI = {
  getAvailable: () => api.get('/roles/available'),
};

// Revision API
export const revisionAPI = {
  request: (taskId, data) => api.post(`/tasks/${taskId}/revision`, data),
  getHistory: (taskId) => api.get(`/tasks/${taskId}/revisions`),
  delete: (taskId, revisionNumber) => api.delete(`/tasks/${taskId}/revisions/${revisionNumber}`),
  getProjectSummary: (projectId) => api.get(`/projects/${projectId}/revision-summary`),
};

// Attachments API
export const attachmentsAPI = {
  upload: (taskId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/tasks/${taskId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAll: (taskId) => api.get(`/tasks/${taskId}/attachments`),
  download: (fileId) => api.get(`/attachments/${fileId}`, { responseType: 'blob' }),
  delete: (taskId, fileId) => api.delete(`/tasks/${taskId}/attachments/${fileId}`),
};

// Auth API
export const authAPI = {
  backdoorLogin: (username, password) => api.post('/auth/backdoor', { username, password }),
  getPermissions: () => api.get('/auth/permissions'),
};

// Audit Log API
export const auditAPI = {
  getLog: (params = {}) => api.get('/audit-log', { params }),
  getStats: () => api.get('/audit-log/stats'),
};

export default api;
